/* eslint-disable no-console */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { httprequest } from "../../data/api";
import { useUserDispatch } from "../../context/UserContext";
import { Icon } from "@iconify/react";
import InputGroup from "../../components/FormComponent/InputGroup";
import logo from "../../assets/images/logo.svg";
import refsignin from "../../assets/images/refsignin.png";
import refsignup from "../../assets/images/refsignup.png";
import "./ReferralAuth.css";

const ReferralAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useUserDispatch();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (isLogin) {
      if (!formData.email || !formData.password) {
        toast.error("Please fill in all fields");
        setIsLoading(false);

        return;
      }

      try {
        const response = await httprequest.post("/api/v1/auth/referrer/login", {
          email: formData.email,
          password: formData.password
        });
        const token = response?.data?.token || response?.data?.data?.token;
        if (token) localStorage.setItem("shelf21-auth", token);

        toast.success(response?.data?.message || "Login successful");
        dispatch({ type: "LOGIN_SUCCESS" });
        setTimeout(() => {
          navigate("/referral/dashboard");
        }, 1000);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Login failed");
      } finally {
        setIsLoading(false);
      }
    } else {
      setPasswordError("");

      if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
        toast.error("Please fill in all fields");
        setIsLoading(false);

        return;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[a-zA-Z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
      if (!passwordRegex.test(formData.password)) {
        setPasswordError("Password should be at least 8 characters, contain a lowercase, uppercase, a number and a special character");
        setIsLoading(false);

        return;
      }

      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        setIsLoading(false);

        return;
      }
      try {
        const response = await httprequest.post("/api/v1/auth/referrer/signup", {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword
        });

        const token = response?.data?.token || response?.data?.data?.token;
        if (token) {
          localStorage.setItem("shelf21-auth", token);
          dispatch({ type: "LOGIN_SUCCESS" });
        }

        toast.success(response?.data?.message || "Signup successful");
        setTimeout(() => {
          setIsLogin(true);
        }, 1000);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Signup failed");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="ref-auth-wrapper">
      <div className="ref-auth-left">
        <div className="ref-auth-logo">
          <img src={logo} alt="Shelf21 Logo" />
        </div>
        <div className="ref-auth-illustration">
          <img src={isLogin ? refsignin : refsignup} alt="Referral Illustration" />
        </div>
        <div className="ref-auth-text-content">
          <h1>Earn rewards by referring schools</h1>
          <p>
            Create your account to track referrals, monitor rewards, and withdraw
            your rewards seamlessly with Shelf21.
          </p>
        </div>
      </div>

      <div className="ref-auth-right">
        <div className="ref-auth-form-container">
          <div className="ref-auth-header">
            {isLogin ? (
              <>
                <h2>Welcome Back</h2>
                <p>Log in to manage your referrals, rewards, and workspace activity.</p>
              </>
            ) : (
              <>
                <h2>Welcome to Shelf21</h2>
                <p>Start your experience with shelf21 by signing up</p>
              </>
            )}
          </div>

          <div className="ref-auth-toggle">
            <button
              className={`ref-auth-toggle-btn ${!isLogin ? "active" : ""}`}
              onClick={() => setIsLogin(false)}
            >
              Sign up
            </button>
            <button
              className={`ref-auth-toggle-btn ${isLogin ? "active" : ""}`}
              onClick={() => setIsLogin(true)}
            >
              Sign in
            </button>
          </div>

          <form className="ref-auth-form" onSubmit={handleSubmit}>
            <div className="ref-auth-input-group">
              <InputGroup
                label={isLogin ? "Email Address" : "Email"}
                inputValue={formData.email}
                onChange={handleChange}
                inputName="email"
                inputType="email"
              />
            </div>

            {!isLogin && (
              <div className="ref-auth-row">
                <div className="ref-auth-input-group half">
                  <InputGroup
                    label="First Name"
                    inputValue={formData.firstName}
                    onChange={handleChange}
                    inputName="firstName"
                    inputType="text"
                    placeHolder="Enter your First Name"
                  />
                </div>
                <div className="ref-auth-input-group half">
                  <InputGroup
                    label="Last Name"
                    inputValue={formData.lastName}
                    onChange={handleChange}
                    inputName="lastName"
                    inputType="text"
                    placeHolder="Enter your Last Name"
                  />
                </div>
              </div>
            )}

            {isLogin ? (
              <div className="ref-auth-input-group">
                <InputGroup
                  label="Password"
                  inputValue={formData.password}
                  onChange={handleChange}
                  inputName="password"
                  inputType={showPassword ? "text" : "password"}
                  placeHolder="Enter your password"
                />
                <div className="ref-auth-eye" onClick={() => setShowPassword(!showPassword)}>
                  <Icon icon={showPassword ? "fluent:eye-16-regular" : "iconamoon:eye-off-light"} />
                </div>
              </div>
            ) : (
              <div className="ref-auth-row">
                <div className="ref-auth-input-group half">
                  <InputGroup
                    label="Password"
                    inputValue={formData.password}
                    onChange={handleChange}
                    inputName="password"
                    inputType={showPassword ? "text" : "password"}
                    placeHolder="Enter your password"
                  />
                  <div className="ref-auth-eye" onClick={() => setShowPassword(!showPassword)}>
                    <Icon icon={showPassword ? "fluent:eye-16-regular" : "iconamoon:eye-off-light"} />
                  </div>
                </div>
                <div className="ref-auth-input-group half">
                  <InputGroup
                    label="Confirm Password"
                    inputValue={formData.confirmPassword}
                    onChange={handleChange}
                    inputName="confirmPassword"
                    inputType={showConfirmPassword ? "text" : "password"}
                    placeHolder="Confirm your password"
                  />
                  <div className="ref-auth-eye" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <Icon icon={showConfirmPassword ? "fluent:eye-16-regular" : "iconamoon:eye-off-light"} />
                  </div>
                </div>
              </div>
            )}

            {!isLogin && passwordError && (
              <p style={{ color: "red", fontSize: "12px", marginTop: "-10px", marginBottom: "10px" }}>
                {passwordError}
              </p>
            )}

            <button type="submit" className="ref-auth-btn" disabled={isLoading}>
              {isLoading ? "Loading..." : isLogin ? "Login" : "Create an account"}
            </button>

            {isLogin && (
              <Link to="/forgot-password" className="ref-auth-recover">
                Recover Password
              </Link>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReferralAuth;
